/* NEXUS theme — interactions & motion. No dependencies. */
(() => {
  'use strict';

  const doc = document.documentElement;
  const routes = window.nxRoutes || {};
  const strings = window.nxStrings || {};
  const settings = window.nxSettings || {};
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  /* ------------------------------------------------------------------ */
  /* Money                                                               */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents) {
    const format = window.nxMoneyFormat || '{{amount}}';
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? withSep + decimal + dec : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot intro + ready state                                            */
  /* ------------------------------------------------------------------ */
  function ready() {
    requestAnimationFrame(() => doc.classList.add('is-ready'));
  }
  function boot() {
    if (!doc.classList.contains('is-booting')) return ready();
    const el = $('.boot');
    const name = $('.boot__name', el);
    if (name) scramble(name);
    setTimeout(() => {
      try { sessionStorage.setItem('nx-booted', '1'); } catch (e) { /* storage blocked */ }
      if (el) el.classList.add('is-done');
      ready();
      setTimeout(() => doc.classList.remove('is-booting'), 950);
    }, 1000);
  }

  /* ------------------------------------------------------------------ */
  /* Text scramble (Latin only — Arabic shaping must stay intact)        */
  /* ------------------------------------------------------------------ */
  const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+=<>/\\[]';
  function scramble(el) {
    const final = el.dataset.text || el.textContent;
    el.dataset.text = final;
    if (reduceMotion || /[^\x00-\x7F]/.test(final)) { el.textContent = final; return; }
    const chars = [...final];
    const queue = chars.map((ch, i) => ({ ch, end: 8 + Math.floor(Math.random() * 14) + Math.floor(i * 0.9) }));
    let frame = 0;
    cancelAnimationFrame(el._scrambleRaf);
    const tick = () => {
      let out = '';
      let done = 0;
      for (const q of queue) {
        if (frame >= q.end || q.ch === ' ') { out += q.ch; done++; }
        else out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
      }
      el.textContent = out;
      frame++;
      if (done < queue.length) el._scrambleRaf = requestAnimationFrame(tick);
    };
    tick();
  }

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll                                                    */
  /* ------------------------------------------------------------------ */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          el.classList.add('is-in');
          if (el.hasAttribute('data-scramble')) scramble(el);
          revealObserver.unobserve(el);
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  function observeReveals(root = document) {
    $$('[data-reveal], [data-scramble], [data-inview]', root).forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Header: scrolled state + hide on scroll down                        */
  /* ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const wrap = header.closest('.shopify-section') || header;
    let lastY = scrollY;
    let ticking = false;
    const update = () => {
      const y = scrollY;
      header.classList.toggle('is-scrolled', y > 10);
      const goingDown = y > lastY;
      const menuOpen = doc.classList.contains('lock');
      wrap.classList.toggle('is-hidden', goingDown && y > 420 && !menuOpen);
      lastY = y;
      ticking = false;
    };
    addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Global animation loop: cursor + marquee skew                        */
  /* ------------------------------------------------------------------ */
  function initLoop() {
    const cursor = settings.cursor && finePointer && !reduceMotion ? $('.cursor') : null;
    const dot = cursor ? $('.cursor-dot') : null;
    const giants = reduceMotion ? [] : $$('.marquee--giant');
    if (!cursor && !giants.length) return;

    let mx = -100, my = -100, cx = -100, cy = -100;
    let lastScroll = scrollY, vel = 0;

    if (cursor) {
      addEventListener('pointermove', (e) => {
        mx = e.clientX; my = e.clientY;
        cursor.classList.add('is-active');
        dot.classList.add('is-active');
        dot.style.transform = `translate3d(${mx}px, ${my}px, 0)`;
      }, { passive: true });
      document.addEventListener('pointerleave', () => { cursor.classList.remove('is-active'); dot.classList.remove('is-active'); });
      document.addEventListener('pointerover', (e) => {
        const hit = e.target.closest('a, button, [data-tilt], label, select, summary, input');
        cursor.classList.toggle('is-hover', !!hit);
      });
    }

    // Only skew the giant marquees that are actually on screen
    const visibleGiants = new Set();
    if (giants.length) {
      const io = new IntersectionObserver((entries) => entries.forEach((e) => (e.isIntersecting ? visibleGiants.add(e.target) : visibleGiants.delete(e.target))));
      giants.forEach((g) => io.observe(g));
    }

    // The loop runs only while something is moving, then sleeps until the next pointer move or scroll
    let raf = 0;
    let lastSkew = 0;
    const loop = () => {
      raf = 0;
      let busy = false;
      if (cursor) {
        cx = lerp(cx, mx, 0.18); cy = lerp(cy, my, 0.18);
        cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
        if (Math.abs(cx - mx) > 0.3 || Math.abs(cy - my) > 0.3) busy = true;
      }
      if (visibleGiants.size) {
        const y = scrollY;
        vel = lerp(vel, y - lastScroll, 0.12);
        lastScroll = y;
        const skew = Math.round(clamp(vel * -0.5, -14, 14) * 10) / 10;
        if (skew !== lastSkew) {
          visibleGiants.forEach((g) => g.style.setProperty('--skew', skew + 'deg'));
          lastSkew = skew;
        }
        if (Math.abs(vel) > 0.05 || skew !== 0) busy = true;
      } else {
        lastScroll = scrollY;
      }
      if (busy) raf = requestAnimationFrame(loop);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
    if (cursor) addEventListener('pointermove', kick, { passive: true });
    if (giants.length) addEventListener('scroll', kick, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Hero: neon grid canvas + 3D card stack parallax                     */
  /* ------------------------------------------------------------------ */
  function hexToRgb(color) {
    const c = color.trim();
    if (c.startsWith('#')) {
      let h = c.slice(1);
      if (h.length === 3) h = h.split('').map((x) => x + x).join('');
      const n = parseInt(h, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const m = c.match(/\d+(\.\d+)?/g);
    return m ? m.slice(0, 3).map(Number) : [0, 225, 255];
  }
  const rgba = (rgb, a) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;

  function initHero(hero) {
    const canvas = $('[data-hero-canvas]', hero);
    const ctx = canvas ? canvas.getContext('2d') : null;
    const css = getComputedStyle(doc);
    const c1 = hexToRgb(css.getPropertyValue('--a1') || '#00e1ff');
    const c2 = hexToRgb(css.getPropertyValue('--a2') || '#8a5cff');
    const c3 = hexToRgb(css.getPropertyValue('--a3') || '#ff3dcb');

    let w = 0, h = 0, t = 0, raf = 0, running = false;
    let mx = 0, my = 0, tmx = 0, tmy = 0, setMx = '', setMy = '', frame = 0;
    // Phones: half the stars, 1x resolution and 30 fps — the grid looks the same, the GPU works far less
    const light = !finePointer;
    const COLS = 26;
    const stars = Array.from({ length: light ? 55 : 110 }, () => ({ x: Math.random(), y: Math.random() * 0.6, z: Math.random(), p: Math.random() * 6.28 }));
    const riders = Array.from({ length: 7 }, () => ({ lane: Math.floor(Math.random() * COLS) - COLS / 2, z: Math.random(), speed: 0.003 + Math.random() * 0.004, c: Math.random() > 0.5 ? c3 : c1 }));

    function resize() {
      if (!canvas) return;
      const dpr = light ? 1 : Math.min(devicePixelRatio || 1, 1.5);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function draw() {
      if (light && running && (frame++ & 1)) { raf = requestAnimationFrame(draw); return; }
      mx = lerp(mx, tmx, light ? 0.12 : 0.06); my = lerp(my, tmy, light ? 0.12 : 0.06);
      // only touch the DOM when the value actually changes (avoids a style recalc every frame)
      const nx = mx.toFixed(3), ny = my.toFixed(3);
      if (nx !== setMx) { hero.style.setProperty('--mx', nx); setMx = nx; }
      if (ny !== setMy) { hero.style.setProperty('--my', ny); setMy = ny; }
      if (!ctx) { t++; if (running) raf = requestAnimationFrame(draw); return; }

      ctx.clearRect(0, 0, w, h);
      // phones stack text over the canvas, so drop the horizon below the copy
      const horizon = h * ((w < 990 ? 0.8 : 0.6) + my * 0.03);
      const vx = w / 2 + mx * w * 0.07;

      // horizon sun glow
      const glow = ctx.createRadialGradient(vx, horizon, 0, vx, horizon, Math.max(w, h) * 0.55);
      glow.addColorStop(0, rgba(c2, 0.38));
      glow.addColorStop(0.4, rgba(c3, 0.08));
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      // stars (one fill colour, alpha via globalAlpha: no new colour string per star per frame)
      ctx.fillStyle = '#ffffff';
      for (const s of stars) {
        ctx.globalAlpha = (0.25 + 0.75 * Math.abs(Math.sin(t * 0.015 + s.p))) * (0.3 + s.z * 0.7);
        const size = 0.6 + s.z * 1.4;
        ctx.fillRect(s.x * w - mx * 24 * s.z, s.y * horizon - my * 12 * s.z, size, size);
      }
      ctx.globalAlpha = 1;

      // floor grid
      ctx.save();
      ctx.beginPath(); ctx.rect(0, horizon, w, h - horizon); ctx.clip();
      const floor = ctx.createLinearGradient(0, horizon, 0, h);
      floor.addColorStop(0, rgba(c1, 0));
      floor.addColorStop(0.18, rgba(c1, 0.5));
      floor.addColorStop(1, rgba(c2, 0.85));
      ctx.strokeStyle = floor;
      ctx.lineWidth = 1;
      const spread = w * 2.4;
      ctx.beginPath();
      for (let i = -COLS / 2; i <= COLS / 2; i++) {
        const xb = vx + (i / (COLS / 2)) * (spread / 2);
        ctx.moveTo(vx + (xb - vx) * 0.02, horizon);
        ctx.lineTo(xb, h);
      }
      const shift = (t * 0.0045) % 1;
      for (let i = 0; i < 24; i++) {
        const p = Math.pow((i + shift) / 24, 2.4);
        const y = horizon + (h - horizon) * p;
        ctx.moveTo(0, y); ctx.lineTo(w, y);
      }
      ctx.stroke();

      // light riders along the grid lanes
      for (const r of riders) {
        r.z += r.speed;
        if (r.z > 1) { r.z = 0; r.lane = Math.floor(Math.random() * COLS) - COLS / 2; }
        const xb = vx + (r.lane / (COLS / 2)) * (spread / 2);
        const x0 = vx + (xb - vx) * 0.02;
        const p1 = Math.pow(r.z, 2.4);
        const p0 = Math.pow(Math.max(0, r.z - 0.08), 2.4);
        const ax = lerp(x0, xb, p0), ay = lerp(horizon, h, p0);
        const mxp = lerp(x0, xb, (p0 + p1) / 2), myp = lerp(horizon, h, (p0 + p1) / 2);
        const bx = lerp(x0, xb, p1), by = lerp(horizon, h, p1);
        // two-step trail (faint tail + bright head) instead of a new gradient per rider per frame
        ctx.lineWidth = 1 + p1 * 3;
        ctx.strokeStyle = rgba(r.c, 0.25);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(mxp, myp); ctx.stroke();
        ctx.strokeStyle = rgba(r.c, 0.9);
        ctx.beginPath(); ctx.moveTo(mxp, myp); ctx.lineTo(bx, by); ctx.stroke();
      }
      ctx.restore();

      // horizon line: layered strokes fake the glow (canvas shadowBlur is very expensive)
      ctx.fillStyle = rgba(c1, 0.1); ctx.fillRect(0, horizon - 4, w, 8);
      ctx.fillStyle = rgba(c1, 0.3); ctx.fillRect(0, horizon - 1.5, w, 3);
      ctx.fillStyle = rgba(c1, 0.95); ctx.fillRect(0, horizon - 0.5, w, 1.2);

      t++;
      if (running) raf = requestAnimationFrame(draw);
    }

    const start = () => { if (!running && !reduceMotion) { running = true; raf = requestAnimationFrame(draw); } };
    const stop = () => { running = false; cancelAnimationFrame(raf); };

    resize();
    draw();
    addEventListener('resize', () => { resize(); if (!running) draw(); });

    if (!reduceMotion) {
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        tmx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        tmy = ((e.clientY - r.top) / r.height - 0.5) * 2;
      }, { passive: true });
      hero.addEventListener('pointerleave', () => { tmx = 0; tmy = 0; });
      addEventListener('deviceorientation', (e) => {
        if (e.gamma == null) return;
        tmx = clamp(e.gamma / 25, -1, 1);
        tmy = clamp((e.beta - 45) / 25, -1, 1);
      }, { passive: true });
      new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop())).observe(hero);
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Tilt, spotlight & magnetic                                          */
  /* ------------------------------------------------------------------ */
  function bindTilt(root = document) {
    if (!finePointer || reduceMotion || !settings.tilt) return;
    $$('[data-tilt]', root).forEach((card) => {
      if (card._tilt) return;
      card._tilt = true;
      let rect = null;
      let raf = 0;
      card.addEventListener('pointerenter', () => { rect = card.getBoundingClientRect(); card.classList.add('is-tilting'); });
      card.addEventListener('pointermove', (e) => {
        if (!rect) rect = card.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width;
        const py = (e.clientY - rect.top) / rect.height;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          card.style.setProperty('--ry', ((px - 0.5) * 14).toFixed(2) + 'deg');
          card.style.setProperty('--rx', ((0.5 - py) * 12).toFixed(2) + 'deg');
          card.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
          card.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
          card.style.setProperty('--gx-n', px.toFixed(3));
        });
      });
      card.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        card.classList.remove('is-tilting');
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
        rect = null;
      });
    });
  }

  function bindSpotlight(root = document) {
    if (!finePointer) return;
    $$('[data-spotlight]', root).forEach((el) => {
      if (el._spot) return;
      el._spot = true;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--gx', (e.clientX - r.left).toFixed(0) + 'px');
        el.style.setProperty('--gy', (e.clientY - r.top).toFixed(0) + 'px');
      }, { passive: true });
    });
  }

  function bindMagnetic(root = document) {
    if (!finePointer || reduceMotion) return;
    $$('[data-magnetic]', root).forEach((el) => {
      if (el._mag) return;
      el._mag = true;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mgx', ((e.clientX - r.left - r.width / 2) * 0.22).toFixed(1) + 'px');
        el.style.setProperty('--mgy', ((e.clientY - r.top - r.height / 2) * 0.35).toFixed(1) + 'px');
      });
      el.addEventListener('pointerleave', () => { el.style.setProperty('--mgx', '0px'); el.style.setProperty('--mgy', '0px'); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Tabs                                                                */
  /* ------------------------------------------------------------------ */
  function bindTabs(root = document) {
    $$('[data-tabs]', root).forEach((wrap) => {
      const list = $('[role="tablist"]', wrap);
      const tabs = $$('[role="tab"]', wrap);
      const pill = $('.tabs__pill', wrap);
      const move = (tab) => {
        if (!pill || !tab) return;
        pill.style.width = tab.offsetWidth + 'px';
        pill.style.transform = `translateX(${tab.offsetLeft}px)`;
      };
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        move(tab);
        if (focus) tab.focus();
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) { bindTilt(panel); bindMagnetic(panel); }
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          const dir = doc.dir === 'rtl' ? -1 : 1;
          let next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + dir + tabs.length) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(i - dir + tabs.length) % tabs.length];
          if (next) { e.preventDefault(); select(next, true); }
        });
      });
      const current = () => tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
      requestAnimationFrame(() => move(current()));
      if (document.fonts) document.fonts.ready.then(() => move(current()));
      addEventListener('resize', () => move(current()));
      if (list && !list.contains(pill)) list.prepend(pill);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Toast + particle burst                                              */
  /* ------------------------------------------------------------------ */
  let toastTimer = 0;
  function toast(message) {
    let el = $('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  }

  function burst(target) {
    if (reduceMotion || !target) return;
    const r = target.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const css = getComputedStyle(doc);
    const colors = ['--a1', '--a2', '--a3'].map((v) => css.getPropertyValue(v).trim());
    for (let i = 0; i < 18; i++) {
      const p = document.createElement('span');
      p.className = 'burst';
      p.style.left = cx + 'px';
      p.style.top = cy + 'px';
      p.style.background = colors[i % 3];
      p.style.boxShadow = `0 0 8px ${colors[i % 3]}`;
      document.body.appendChild(p);
      const angle = (Math.PI * 2 * i) / 18 + Math.random() * 0.4;
      const dist = 40 + Math.random() * 60;
      p.animate([
        { transform: 'translate(-50%, -50%) scale(1) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${Math.cos(angle) * dist}px), calc(-50% + ${Math.sin(angle) * dist}px)) scale(0) rotate(${Math.random() * 360}deg)`, opacity: 0 }
      ], { duration: 700 + Math.random() * 300, easing: 'cubic-bezier(.16,1,.3,1)' }).onfinish = () => p.remove();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  const cart = {
    get el() { return $('[data-cart-drawer]'); },
    lastFocus: null,
    open() {
      const d = this.el;
      if (!d) return;
      this.lastFocus = document.activeElement;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      doc.classList.add('lock');
      setTimeout(() => { const c = $('[data-cart-close]', d); if (c) c.focus(); }, 80);
    },
    close() {
      const d = this.el;
      if (!d || !d.classList.contains('is-open')) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      doc.classList.remove('lock');
      if (this.lastFocus) this.lastFocus.focus();
    },
    async refresh() {
      const targets = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page) targets.push(page.dataset.sectionId);
      const res = await fetch(`${routes.root}?sections=${targets.join(',')}`);
      const data = await res.json();
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      if (data['cart-drawer']) {
        const fresh = parse(data['cart-drawer']).querySelector('[data-cart-drawer-inner]');
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) {
          current.replaceWith(fresh);
          const count = fresh.dataset.count;
          $$('[data-cart-count]').forEach((el) => {
            el.textContent = count === '0' ? '' : count;
            el.dataset.count = count;
            el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump');
          });
        }
      }
      if (page && data[page.dataset.sectionId]) {
        const fresh = parse(data[page.dataset.sectionId]).querySelector('[data-cart-page]');
        if (fresh) page.replaceWith(fresh);
      }
    },
    async change(line, quantity) {
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || strings.error);
      }
      await this.refresh();
    }
  };

  function initCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]') || $(`[form="${form.id}"]`);
      if (btn && btn.disabled) return;
      // the form is novalidate (styled errors), so check required fields like WhatsApp here
      const invalid = $$('input[required]', form).find((i) => !i.value.trim());
      if (invalid) {
        invalid.closest('.field')?.classList.add('is-invalid');
        invalid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        setTimeout(() => invalid.focus({ preventScroll: true }), 300);
        return;
      }
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
      try {
        const res = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || strings.error);
        if (btn) { btn.classList.add('is-added'); burst(btn); }
        await cart.refresh();
        setTimeout(() => cart.open(), 260);
        setTimeout(() => btn && btn.classList.remove('is-added'), 1800);
      } catch (err) {
        toast(err.message || strings.error);
      } finally {
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
      }
    });

    document.addEventListener('click', async (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener && !$('[data-cart-page]')) {
        e.preventDefault();
        cart.open();
        return;
      }
      if (e.target.closest('[data-cart-close]')) { cart.close(); return; }
      const changer = e.target.closest('[data-line-change]');
      if (changer) {
        e.preventDefault();
        const item = changer.closest('.line-item');
        if (item) item.classList.add('is-updating');
        try { await cart.change(Number(changer.dataset.line), Number(changer.dataset.qty)); }
        catch (err) { toast(err.message); if (item) item.classList.remove('is-updating'); }
      }
    });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') cart.close(); });
  }

  /* ------------------------------------------------------------------ */
  /* Mobile menu                                                         */
  /* ------------------------------------------------------------------ */
  function initMenu() {
    const drawer = $('[data-menu-drawer]');
    if (!drawer) return;
    const openers = $$('[data-menu-open]');
    const open = (btn) => {
      const r = btn.getBoundingClientRect();
      drawer.style.setProperty('--cx', `${r.left + r.width / 2}px`);
      drawer.style.setProperty('--cy', `${r.top + r.height / 2}px`);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      doc.classList.add('lock');
      setTimeout(() => { const c = $('[data-menu-close]', drawer); if (c) c.focus(); }, 100);
    };
    const close = () => {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      doc.classList.remove('lock');
      if (openers[0]) openers[0].focus();
    };
    openers.forEach((b) => b.addEventListener('click', () => open(b)));
    $$('[data-menu-close]', drawer).forEach((b) => b.addEventListener('click', close));
    $$('a', drawer).forEach((a) => a.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* ------------------------------------------------------------------ */
  /* Predictive search                                                   */
  /* ------------------------------------------------------------------ */
  function initSearch() {
    const overlay = $('[data-search-overlay]');
    if (!overlay) return;
    const input = $('input[type="search"]', overlay);
    const results = $('[data-search-results]', overlay);
    let timer = 0;
    let controller = null;
    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      doc.classList.add('lock');
      setTimeout(() => input.focus(), 60);
    };
    const close = () => {
      if (!overlay.classList.contains('is-open')) return;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      doc.classList.remove('lock');
      if (lastFocus) lastFocus.focus();
    };

    $$('[data-search-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    $$('[data-search-close]', overlay).forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if (e.key === '/' && !/input|textarea|select/i.test(document.activeElement.tagName)) { e.preventDefault(); open(); }
    });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection&resources[limit]=8&resources[options][unavailable_products]=last`;
          const res = await fetch(url, { signal: controller.signal });
          const html = await res.text();
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive]');
          results.innerHTML = fresh ? fresh.innerHTML : '';
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = '';
        }
      }, 180);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */
  function initProduct(root = document) {
    $$('[data-product]', root).forEach((p) => {
      const json = $('[data-variants-json]', p);
      const form = $('form[data-product-form]', p);
      const mainMedia = $('.product__main-media', p);

      const showMedia = (thumb) => {
        if (!thumb || !mainMedia) return;
        const img = $('img', mainMedia);
        $$('[data-thumb]', p).forEach((t) => t.classList.toggle('is-active', t === thumb));
        if (!img) return;
        mainMedia.classList.add('is-swapping');
        setTimeout(() => {
          img.srcset = thumb.dataset.srcset;
          img.src = thumb.dataset.src;
          mainMedia.style.setProperty('--ar', thumb.dataset.ar);
          img.onload = () => mainMedia.classList.remove('is-swapping');
          setTimeout(() => mainMedia.classList.remove('is-swapping'), 400);
        }, 180);
      };
      $$('[data-thumb]', p).forEach((t) => t.addEventListener('click', () => showMedia(t)));

      // WhatsApp order link: prefilled with the game, the chosen options and the quantity
      const waLink = $('[data-wa-order]', p);
      const qtyInput = $('[data-qty-input]', p);
      let currentVariantTitle = '';
      const updateWa = () => {
        if (!waLink) return;
        const msg = waLink.dataset.waTemplate
          .replace('%TITLE%', waLink.dataset.waTitle)
          .replace('%VARIANT%', currentVariantTitle)
          .replace('%QTY%', qtyInput ? qtyInput.value : '1');
        waLink.href = `https://wa.me/${waLink.dataset.waNumber}?text=${encodeURIComponent(msg)}`;
      };

      // remember the customer's WhatsApp number between products
      const waField = $('[data-wa-field]', p);
      if (waField) {
        try { waField.value = localStorage.getItem('nx-wa') || ''; } catch (e) { /* storage blocked */ }
        waField.addEventListener('input', () => {
          waField.closest('.field')?.classList.remove('is-invalid');
          try { localStorage.setItem('nx-wa', waField.value.trim()); } catch (e) { /* storage blocked */ }
        });
      }

      if (json && form) {
        const variants = JSON.parse(json.textContent);
        const idInput = $('input[name="id"]', form);
        const addBtns = $$('[data-add-btn]', p);
        const buyNow = $('[data-buy-now]', p);
        const preorder = p.dataset.preorder === 'true';
        const update = () => {
          const selected = $$('[data-option]', p).map((fs) => {
            const c = $('input:checked', fs);
            return c ? c.value : null;
          });
          const v = variants.find((x) => x.options.every((o, i) => o === selected[i]));
          if (!v) {
            addBtns.forEach((b) => { b.disabled = true; const l = $('[data-add-label]', b); if (l) l.textContent = strings.unavailable; });
            return;
          }
          idInput.value = v.id;
          currentVariantTitle = v.title;
          const pending = v.price === 0;
          $$('[data-price]', p).forEach((el) => {
            el.textContent = pending ? strings.priceSoon : formatMoney(v.price);
            el.closest('.price')?.classList.toggle('price--soon', pending);
          });
          const onSale = !pending && v.compare_at_price && v.compare_at_price > v.price;
          $$('[data-compare]', p).forEach((el) => {
            el.hidden = !onSale;
            el.textContent = onSale ? formatMoney(v.compare_at_price) : '';
          });
          $$('[data-save]', p).forEach((el) => {
            el.hidden = !onSale;
            el.textContent = onSale ? strings.save.replace('%AMOUNT%', formatMoney(v.compare_at_price - v.price)) : '';
          });
          addBtns.forEach((b) => {
            b.disabled = !v.available || pending;
            const l = $('[data-add-label]', b);
            if (!l) return;
            if (pending) l.textContent = strings.comingSoon;
            else if (!v.available) l.textContent = strings.soldOut;
            else l.textContent = preorder ? strings.preorder : strings.addToCart;
          });
          if (buyNow) buyNow.hidden = pending;
          updateWa();
          const url = new URL(location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState(null, '', url);
          if (v.featured_media) showMedia($(`[data-thumb][data-media-id="${v.featured_media.id}"]`, p));
        };
        p.addEventListener('change', (e) => { if (e.target.closest('[data-option]')) update(); });
        const initial = variants.find((x) => String(x.id) === idInput.value);
        currentVariantTitle = initial ? initial.title : '';
      }
      updateWa();
      if (qtyInput) qtyInput.addEventListener('input', updateWa);

      // quantity steppers
      p.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-step]');
        if (!step) return;
        const input = $('input', step.closest('.qty'));
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.qtyStep));
        updateWa();
      });

      // sticky add-to-cart bar (mobile)
      const sticky = $('[data-sticky-atc]', p);
      const mainBtn = $('.product__form [data-add-btn]', p);
      if (sticky && mainBtn && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', show);
          doc.classList.toggle('has-sticky-atc', show);
        }).observe(mainBtn);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Product recommendations                                             */
  /* ------------------------------------------------------------------ */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (el) => {
      if (!el.dataset.url) return;
      try {
        const res = await fetch(el.dataset.url);
        const html = await res.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          observeReveals(el);
          bindTilt(el);
          bindMagnetic(el);
        }
      } catch (e) { /* recommendations are optional */ }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Shared-element page transition: card image → product hero image     */
  /* ------------------------------------------------------------------ */
  function initViewTransitions() {
    if (!settings.pageTransitions || reduceMotion) return;
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-vt-link]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
      $$('[data-vt-active]').forEach((x) => { x.style.viewTransitionName = ''; x.removeAttribute('data-vt-active'); });
      const card = link.closest('.card');
      const media = card ? $('.card__media img, .card__media svg', card) : null;
      if (media) { media.style.viewTransitionName = 'hero-media'; media.setAttribute('data-vt-active', ''); }
    });
    addEventListener('pageshow', () => {
      $$('[data-vt-active]').forEach((x) => { x.style.viewTransitionName = ''; x.removeAttribute('data-vt-active'); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Pause looping CSS effects while they are off screen                 */
  /* ------------------------------------------------------------------ */
  const pauseObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => e.target.classList.toggle('is-paused', !e.isIntersecting)), { rootMargin: '100px 0px' })
    : null;
  function pauseOffscreen(root = document) {
    if (!pauseObserver) return;
    $$('.glow-border, .marquee, .gen-cover, .wa-float', root).forEach((el) => {
      if (el._paused) return;
      el._paused = true;
      pauseObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* FAQ: own EN/AR switch + "show more" into a scrollable list          */
  /* ------------------------------------------------------------------ */
  function initFaq(root = document) {
    $$('[data-faq]', root).forEach((faq) => {
      if (faq._faq) return;
      faq._faq = true;
      const list = $('[data-faq-list]', faq);
      const more = $('[data-faq-more]', faq);
      const langBtns = $$('[data-faq-lang]', faq);
      const pill = $('.faq__lang-pill', faq);

      const movePill = () => {
        const on = langBtns.find((b) => b.getAttribute('aria-pressed') === 'true');
        if (!pill || !on) return;
        pill.style.width = on.offsetWidth + 'px';
        pill.style.transform = `translateX(${on.offsetLeft}px)`;
      };
      const setLang = (lang, remember) => {
        faq.dataset.lang = lang;
        faq.dir = lang === 'ar' ? 'rtl' : 'ltr';
        langBtns.forEach((b) => b.setAttribute('aria-pressed', b.dataset.faqLang === lang ? 'true' : 'false'));
        requestAnimationFrame(movePill);
        if (remember) { try { localStorage.setItem('nx-faq-lang', lang); } catch (e) { /* storage blocked */ } }
      };
      langBtns.forEach((b) => b.addEventListener('click', () => setLang(b.dataset.faqLang, true)));
      try { const saved = localStorage.getItem('nx-faq-lang'); if (saved && langBtns.length) setLang(saved, false); } catch (e) { /* storage blocked */ }
      requestAnimationFrame(movePill);
      if (document.fonts) document.fonts.ready.then(movePill);

      if (!more || !list) return;
      const moreLabel = $('[data-faq-more-label]', faq);
      const lessLabel = $('[data-faq-less-label]', faq);
      const updateEnd = () => faq.classList.toggle('is-at-end', list.scrollTop + list.clientHeight >= list.scrollHeight - 4);
      list.addEventListener('scroll', updateEnd, { passive: true });

      more.addEventListener('click', () => {
        const expanding = !faq.classList.contains('is-expanded');
        const from = list.offsetHeight;
        faq.classList.toggle('is-expanded', expanding);
        more.setAttribute('aria-expanded', String(expanding));
        moreLabel.hidden = expanding;
        lessLabel.hidden = !expanding;
        $$('.faq__item--extra', list).forEach((item) => item.classList.add('is-in'));
        if (!expanding) list.scrollTop = 0;
        const to = list.offsetHeight;
        if (!reduceMotion) list.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: 550, easing: 'cubic-bezier(.16,1,.3,1)' });
        updateEnd();
        if (!expanding) faq.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Collection sort                                                     */
  /* ------------------------------------------------------------------ */
  function initSort() {
    $$('[data-sort]').forEach((sel) => sel.addEventListener('change', () => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      location.href = url.toString();
    }));
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */
  function init() {
    boot();
    initHeader();
    observeReveals();
    $$('[data-hero]').forEach(initHero);
    bindTilt();
    bindSpotlight();
    bindMagnetic();
    bindTabs();
    initLoop();
    initCart();
    initMenu();
    initSearch();
    initProduct();
    initRecommendations();
    initViewTransitions();
    initSort();
    initFaq();
    pauseOffscreen();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    observeReveals(e.target);
    bindTilt(e.target);
    bindSpotlight(e.target);
    bindMagnetic(e.target);
    bindTabs(e.target);
    initProduct(e.target);
    initFaq(e.target);
    pauseOffscreen(e.target);
    $$('[data-hero]', e.target).forEach(initHero);
  });
})();
